import { useTranslation } from 'next-i18next/pages'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { Banner, BREAKPOINTS, Button, Form, Radio, RadioList, TextField, ToggleRadio } from '@boltenergy-be/design-system'
import { ProducerContactFormInputs } from 'features/producer-contact-request/producer-contact-request-form/types'
import { useState } from 'react'
import { trackGTMEvent } from 'utils/tracking'
import { CompanyName, CompanyNumber, Email, FirstName, LastName, Phone } from 'components/inputs'
import { EnergyType } from 'types/producer'
import { getEnergyNameI18Key } from 'utils/producers'
import styles from './ProducerContactRequestForm.module.scss'
import EnergyTypeIcon from 'components/producers/energy-type-icon/EnergyTypeIcon'
import { useWindowSize } from 'usehooks-ts'
import backendLib from 'lib/bolt-backend'
import { useRouter } from 'next/router'
import { PRODUCER_ENERGY_TYPES, PRODUCER_INSTALLATION_TYPES, ProducerRequestRequestBody } from '@boltenergy-be/shared'
import { getUpperCaseLanguage } from 'utils/format'

const ProducerContactRequestForm = () => {
  // Router
  const { locale } = useRouter()

  // i18n
  const { t: tForm } = useTranslation('b2b', { keyPrefix: 'request' })
  const { t } = useTranslation(['validation', 'common'])

  // Local state
  const [error, setError] = useState<boolean>(false)
  const [sending, setSending] = useState<boolean>(false)
  const [success, setSuccess] = useState<boolean>(false)

  // Window size
  const { width } = useWindowSize({ initializeWithValue: false })
  const isTablet = !!width && width < BREAKPOINTS.tablet // Based on $widthDesktop

  // React hook form
  const hookForm = useForm<ProducerContactFormInputs>({
    defaultValues: {
      installationType: PRODUCER_INSTALLATION_TYPES.existingInstallation,
      withUsage: 'yes',
      usage: {
        energyNeed: 'electricity'
      }
    },
    mode: 'onBlur'
  })
  const {
    formState: { errors },
    handleSubmit,
    register,
    control,
    clearErrors
  } = hookForm
  const watchEnergyType = useWatch({ control, name: 'energyType' })
  const watchWithUsage = useWatch({ control, name: 'withUsage' })
  const watchEnergyNeed = useWatch({ control, name: 'usage.energyNeed' })
  const watchPowerCapacity = useWatch({ control, name: 'powerCapacity' })
  const watchSolarPanels = useWatch({ control, name: 'numberOfSolarPanels' })

  /**
   * Sends the producer request to the API
   * Shows an error or confirmation depending on API response
   *
   * @param {ProducerContactFormInputs} data
   */
  const onSubmit = async (data: ProducerContactFormInputs) => {
    setSending(true)
    setError(false)

    const { company, contact, usage, withUsage, ...installation } = data

    // Prepare data & send it to the API
    const requestData: ProducerRequestRequestBody = {
      account: { company },
      contact: {
        ...contact,
        language: getUpperCaseLanguage(locale)
      },
      installation: {
        injectionCapacity: Number(installation.injectionCapacity),
        installationType: installation.installationType,
        energyType: installation.energyType,
        numberOfSolarPanels: installation.numberOfSolarPanels ? Number(installation.numberOfSolarPanels) : undefined,
        powerCapacity: Number(installation.powerCapacity)
      },
      usage:
        withUsage === 'yes'
          ? {
              estimatedYearlyElectricityUsage: Number(usage.estimatedYearlyElectricityUsage),
              estimatedYearlyGasUsage: usage.estimatedYearlyGasUsage ? Number(usage.estimatedYearlyGasUsage) : undefined,
              needsGas: usage.energyNeed === 'electricityAndGas'
            }
          : undefined
    }

    try {
      const { success } = await backendLib.postProducerRequest(requestData)

      trackGTMEvent('producer_contact_finished')

      if (success) {
        setSuccess(true)
      } else {
        setError(true)
      }
    } catch (error) {
      setError(true)
    }

    setSending(false)
  }

  return (
    <FormProvider {...hookForm}>
      <Form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
        {/* INSTALLATION DETAILS */}
        <Form.Fieldset title={tForm('installation.title')}>
          <ToggleRadio
            hiddenLabel
            label={tForm('installation.type.label')}
            name="installationType"
            error={errors?.installationType?.message}
            grid="full"
            options={[
              {
                ...register('installationType', { required: t('required') }),
                label: tForm('installation.type.existing', { context: isTablet ? 'mobile' : 'desktop' }),
                value: PRODUCER_INSTALLATION_TYPES.existingInstallation,
                id: 'existing'
              },
              {
                ...register('installationType', { required: t('required') }),
                label: tForm('installation.type.new', { context: isTablet ? 'mobile' : 'desktop' }),
                value: PRODUCER_INSTALLATION_TYPES.newInstallation,
                id: 'new'
              }
            ]}
          />

          <RadioList
            label={tForm('installation.energyTypes.label')}
            grid="full"
            listClassName={styles['fancy-radio-list']}
            error={errors?.energyType?.message}
          >
            {Object.values(PRODUCER_ENERGY_TYPES).map((type) => (
              <Radio
                {...register('energyType', { required: t('required') })}
                key={type}
                id={type}
                value={type}
                label={
                  <>
                    <EnergyTypeIcon energyType={type} className={styles.icon} /> <span>{t(getEnergyNameI18Key(type))}</span>
                  </>
                }
                className={styles['radio-input']}
                wrapperClassName={styles['radio-wrapper']}
              />
            ))}
          </RadioList>

          {watchEnergyType === EnergyType.SOLAR && (
            <>
              <TextField
                {...register('numberOfSolarPanels', {
                  validate: (value) => {
                    // Not required when power capacity value is given
                    if (watchPowerCapacity) return true

                    return !!value || t('required')
                  },
                  onChange: (event) => {
                    if (event.target.value) {
                      clearErrors('powerCapacity')
                    }
                  }
                })}
                error={errors?.numberOfSolarPanels?.message}
                label={tForm('installation.solarPanelAmount.label')}
                type="number"
              />

              <Form.EmptyField />
            </>
          )}

          <TextField
            {...register('powerCapacity', {
              validate: (value) => {
                // Not required when energy type === solar & has solar panels input
                if (watchEnergyType === EnergyType.SOLAR && !!watchSolarPanels) return true

                return !!value || t('required')
              },
              onChange: (event) => {
                if (watchEnergyType === EnergyType.SOLAR) {
                  if (event.target.value) {
                    clearErrors('numberOfSolarPanels')
                  }
                }
              }
            })}
            error={errors?.powerCapacity?.message}
            label={tForm('installation.installedCapacity.label')}
            suffix={{ as: tForm('installation.installedCapacity.unit') }}
            type="number"
          />

          <TextField
            {...register('injectionCapacity', { required: t('required') })}
            error={errors?.injectionCapacity?.message}
            label={tForm('installation.estimatedInjection.label')}
            suffix={{ as: tForm('installation.estimatedInjection.unit') }}
            type="number"
          />
        </Form.Fieldset>

        <Form.Fieldset title={tForm('usage.title')}>
          <ToggleRadio
            label={tForm('usage.withUsage.label')}
            name="withUsage"
            error={errors?.withUsage?.message}
            grid="full"
            options={[
              {
                ...register('withUsage', { required: t('required') }),
                label: t('common:yes'),
                value: 'yes',
                id: 'yes'
              },
              {
                ...register('withUsage', { required: t('required') }),
                label: t('common:no'),
                value: 'no',
                id: 'no'
              }
            ]}
          />

          {watchWithUsage === 'yes' && (
            <>
              <ToggleRadio
                label={tForm('usage.energyNeed.label')}
                name="usage.energyNeed"
                error={errors?.usage?.energyNeed?.message}
                grid="full"
                options={[
                  {
                    ...register('usage.energyNeed', { required: t('required') }),
                    label: t('common:electricity'),
                    value: 'electricity',
                    id: 'electricity'
                  },
                  {
                    ...register('usage.energyNeed', { required: t('required') }),
                    label: t('common:electricityAndGas'),
                    value: 'electricityAndGas',
                    id: 'electricityAndGas'
                  }
                ]}
              />

              <TextField
                {...register('usage.estimatedYearlyElectricityUsage', { required: t('required') })}
                error={errors?.usage?.estimatedYearlyElectricityUsage?.message}
                label={tForm('usage.estimatedUsageElectricity.label')}
                suffix={{ as: tForm('usage.estimatedUsageElectricity.unit') }}
                type="number"
              />

              {watchEnergyNeed === 'electricityAndGas' && (
                <TextField
                  {...register('usage.estimatedYearlyGasUsage', { required: t('required') })}
                  error={errors?.usage?.estimatedYearlyGasUsage?.message}
                  label={tForm('usage.estimatedUsageGas.label')}
                  suffix={{ as: tForm('usage.estimatedUsageGas.unit') }}
                  type="number"
                />
              )}
            </>
          )}
        </Form.Fieldset>

        {/* CONTACT PERSON */}
        <Form.Fieldset title={tForm('contactPerson.title')}>
          <FirstName parentDotPath="contact" />

          <LastName parentDotPath="contact" />

          <Email parentDotPath="contact" />

          <Phone parentDotPath="contact" identifier="phoneMobile" />
        </Form.Fieldset>

        {/* COMPANY DETAILS */}
        <Form.Fieldset title={tForm('company.title')}>
          <CompanyName parentDotPath="company" identifier="name" />

          <CompanyNumber prefixed parentDotPath="company" identifier="enterpriseNumber" />
        </Form.Fieldset>

        <Form.Footer>
          {!success && (
            <Button loading={sending} disabled={success || error} type="submit" isFullwidth trailingIcon="chevronRight">
              {tForm('sendRequest')}
            </Button>
          )}

          {success && (
            <Banner className="mt-500" type="positive">
              {tForm('confirmation')}
            </Banner>
          )}

          {!sending && error && (
            <Banner className="mt-500" type="blocking">
              {t('common:error')}
            </Banner>
          )}
        </Form.Footer>
      </Form>
    </FormProvider>
  )
}

export default ProducerContactRequestForm
